import { Box, Grid } from "@mui/material";
import { setInnCode } from "components/app/App.slice";
import Feedback from "components/common/Feedback";
import NoPlansBanner from "components/common/NoPlansBanner";
import Scroller from "components/common/Scroller/InfiniteScroller";
import SearchAndFilterRow from "components/common/SearchAndFilterRow";
import ActionCard from "components/features/ActionCard";
import MultiSelectButtons from "components/features/ActionCard/MultiSelectButtons";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { PAGE_SIZE, messages } from "utils/CommonConstants";
import { getHltInncodes } from "utils/CommonFunctions";
import { pageNames, planTypes } from "utils/Types";
import { fetchIndividualActionAsync, fetchIndividualActionDetailAsync } from "./individualAction.slice";

function IndividualAction() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchText = searchParams.get("q") as string;
  const cardId = searchParams.get("cardid") as string;
  const [pageNo, setPageNo] = useState(1);
  const innCode = useAppSelector((state: any) => state.app.innCode);
  const localSearch = useAppSelector((state) => state.app.searchText);
  const isAdmin = useAppSelector((state) => state.app.isAdmin);
  const innCodes = getHltInncodes();
  const [multipleInncodeAction, setmultipleInncodeAction] = useState(false);
  const [multipleInncodeFeedback, setmultipleInncodeFeedback] = useState<any>();
  const individualActionList = useAppSelector((state: any) => state.individualAction.list);
  const individualActionLoader = useAppSelector((state: any) => state.individualAction.actionShowLoader);
  const individualActionDetail = useAppSelector((state: any) => state.individualAction.actionDetails);
  const totalRecords = useAppSelector((state: any) => state.individualAction.totalRecords);
  const selectedFunctionalAreas = useAppSelector((state: any) => state.app.selectedFunctionalAreas);
  const filterIds = useAppSelector((state: any) => state.app.filterIds);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const selectedMyPlanDetail: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);

  useEffect(() => {
    const props: any = {
      filter: {
        functionalAreaIds: id ? [id] : selectedFunctionalAreas,
        filterIds,
        innCode
      },
      pagination: {
        page: 1,
        size: PAGE_SIZE
      },
      searchText: searchText || localSearch
    };
    dispatch(fetchIndividualActionAsync(props));
    setPageNo(1);
  }, [dispatch, id, filterIds, innCode, selectedFunctionalAreas, searchText, localSearch]);

  useEffect(() => {
    if (cardId) {
      const otherInnCode = individualActionDetail?.meta?.cardAvailableInncodes?.split(",");
      const hasOtherInncode = otherInnCode?.length > 0;
      if (individualActionDetail?.id) {
        setmultipleInncodeFeedback({
          innCode: multipleInncodeFeedback?.innCode,
          otherInnCodes: individualActionDetail?.meta?.cardAvailableInncodes?.split(",")
        });
        dispatch(setInnCode(multipleInncodeFeedback?.innCode));
        if (hasOtherInncode) setmultipleInncodeAction(true);
        navigate(location.pathname + "?q=" + searchText, {
          state: { backURL: "/library", cardId: cardId }
        });
        return;
      }

      noAccessRedirect();

      innCodes?.splice(innCodes?.indexOf(hasOtherInncode ? otherInnCode[0] : innCode), 1);
      const props: any = {
        id: cardId,
        innCode: hasOtherInncode ? otherInnCode[0] : innCode,
        otherInnCodes: innCodes
      };
      setmultipleInncodeFeedback({
        innCode: hasOtherInncode ? otherInnCode[0] : innCode
      });
      dispatch(fetchIndividualActionDetailAsync(props));
    }
  }, [dispatch, cardId, individualActionDetail]);

  const noAccessRedirect = () => {
    const noAccessCondition =
      (Object.keys(individualActionDetail).length !== 0 && !individualActionDetail?.meta?.cardAvailableInncodes) ||
      individualActionDetail?.meta?.cardAvailableInncodes === null;
    if (noAccessCondition)
      navigate(`/`, { state: { hasNoAccess: true, message: "You don't have access to view this action!" } });
  };

  const fetchMore = () => {
    const props = {
      filter: {
        functionalAreaIds: id ? [id] : selectedFunctionalAreas,
        filterIds,
        innCode
      },
      pagination: {
        page: pageNo + 1,
        size: PAGE_SIZE
      },
      searchText: searchText || localSearch
    };
    dispatch(fetchIndividualActionAsync(props));
    setPageNo(pageNo + 1);
  };

  return (
    <>
      <Feedback
        form="copyAction"
        message={messages.actionAdded}
        linkLabel="Go to my plans"
        href={`/myplandetails/${selectedMyPlanDetail?.id}`}
      />
      <Feedback
        form=""
        actionStatus={{ shouldShow: multipleInncodeAction, variant: "warning" }}
        message={"You are viewing Action details for " + multipleInncodeFeedback?.innCode + "."}
      />
      <Feedback
        form="addToMyPlans"
        message={messages.planAdded}
        linkLabel="Go to my plans"
        href={`/myplandetails/${selectedMyPlanDetail?.id}`}
      />

      <Box>
        <Grid item md={12}>
          <SearchAndFilterRow id={id} searchText={searchText} inputText={localSearch} isPlanTypeVisible={false} />
        </Grid>
        {!isAdmin && individualActionList.length > 0 && (
          <Grid item md={12} marginY="30px" display="flex" flexWrap="wrap" gap={"8px"}>
            <MultiSelectButtons
              planType={planTypes.AllPlans}
              multiCardSelect={multiSelectMode}
              handleMultiSelectMode={() => setMultiSelectMode(!multiSelectMode)}
            />
          </Grid>
        )}
      </Box>

      {individualActionList.length > 0 ? (
        <Scroller
          dataLength={individualActionList.length}
          loadMore={fetchMore}
          totalList={totalRecords}
          label="Actions"
        >
          <Grid
            container
            display="flex"
            flexDirection="row"
            justifyContent={"flex-start"}
            flexWrap="wrap"
            width={"100%"}
            gap={"40px"}
          >
            {individualActionList?.map((element: any) => {
              return (
                <ActionCard
                  multiCardSelect={multiSelectMode}
                  key={element.id}
                  item={element}
                  planType={planTypes.AllPlans}
                />
              );
            })}
          </Grid>
        </Scroller>
      ) : (
        <>
          {!individualActionLoader && (
            <Box marginBottom={"50px"}>
              <NoPlansBanner
                type={id ? pageNames.searchResultAction : pageNames.globalSearchResult}
                pageName={pageNames.searchResultAction}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
}

export default IndividualAction;
